/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isObject from 'lodash-es/isObject';
import set from 'lodash-es/set';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath } from 'react-router-dom';

import { copy, getStateString, getTileEstimate, paste } from '../../api/ventura/adGroup';
import TARGETING_TACTICS from '../../constants/adGroupTargeting';
import { API_CONTRACT_SEARCH_URL, API_LINE_ITEM_SEARCH_URL } from '../../constants/apiUrls';
import { useConfig } from '../../hooks/useConfig';
import { useSnackBar } from '../../hooks/useSnackBar';
import { getAdGroupCloneWarnings } from '../../util/cloneWarnings';
import { isBlank } from '../../util/helpers';
import posthog from '../../util/posthog';
import { getRequestJSON } from '../../util/services';
import Checkbox from '../forms/Checkbox';
import CloneFieldWarnings from '../forms/CloneFieldWarnings';
import FormEndOptions from '../forms/FormEndOptions';
import NumericTextField from '../forms/NumericTextField';
import TextField from '../forms/TextField';
import TypeaheadTextField from '../forms/TypeaheadTextField';

interface CloneAdGroupFormProps {
  adGroup: any;
  onClose?: Function;
}

function CloneAdGroupForm({ adGroup, onClose }: CloneAdGroupFormProps) {
  const adGroupId = adGroup.id;
  const { apiHost, apiHostFor } = useConfig();
  const { showSnackBar } = useSnackBar();
  const [isShowDetails, setShowDetails] = useState(false);
  const [cloneDifferentParent, setCloneDifferentParent] = useState(false);
  const defaultValues = {
    id: null,
    admin_campaign: null,
    copy_locations: false,
    copy_targeted_creatives: false,
    selectedContractId: null,
    selectedLineItemId: null,
    number_of_copies: 1,
    // ADOPS-2722 — deselect toggle for time parting; default true preserves current
    // copy behavior unless the user opts out (see submitHandler).
    copy_time_parting: true,
  };

  const form = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  const { data: adminCampaign } = useQuery({
    queryKey: ['copyAdGroup', adGroupId],
    queryFn: () => copy(apiHost, adGroupId),
  });

  const { data: stateString } = useQuery({
    queryKey: ['getStateString', adGroupId, isShowDetails],
    queryFn: () => isShowDetails && getStateString(apiHost, adGroupId),
  });

  const { data: tileEstimate } = useQuery({
    queryKey: ['getTileEstimate', adGroupId, isShowDetails],
    queryFn: () => isShowDetails && getTileEstimate(apiHost, adGroupId),
  });

  const { reset } = form;

  const pasteMutation = useMutation({
    mutationFn: (payload) => paste(apiHost, adGroupId, payload),
    onSuccess: (response) => {
      posthog.capture('ad_group_duplicated', { source_ad_group_id: adGroupId });
      showSnackBar(response.status, 'success', { event: 'ad_group_duplicated', source_ad_group_id: adGroupId });
      if (onClose) {
        onClose();
      }
    },
  });

  useEffect(() => {
    if (adminCampaign) {
      reset({
        id: adGroupId,
        copy_locations: true,
        copy_targeted_creatives: true,
        admin_campaign: adminCampaign,
        number_of_copies: defaultValues.number_of_copies,
        copy_time_parting: true,
      });
    }
  }, [reset, adminCampaign, adGroupId, defaultValues.number_of_copies]);

  const targetingTacticsLabels = {};
  Object.values(TARGETING_TACTICS).forEach((item) => {
    if (typeof item !== 'boolean') {
      targetingTacticsLabels[item?.key.toLowerCase()] = item?.label;
    }
  });

  // Converted 'lorem_ipsum' to 'Lorem Ipsum'
  // It is used when the targeting tactic has no label defined in TARGETING_TACTICS
  // So we will just convert the snake case key to something somewhat readable.
  const snakeCaseToSentenceCase = (snakeCase) =>
    snakeCase.replace(/(^|_)([a-z])/g, (unused1, unused2, firstLetter) => ` ${firstLetter.toUpperCase()}`);

  const normalizedAdGroupTargeting = Object.entries(adGroup.targetingProfile || {})
    .filter(([, value]) => (isObject(value) ? !isEmpty(value) : value))
    .map(([key, value]) => ({
      label: targetingTacticsLabels[key.toLowerCase()] || snakeCaseToSentenceCase(key),
      value: JSON.stringify(value),
    }));

  const { handleSubmit, watch } = form;

  const [selectedContractId, selectedLineItemId] = watch(['selectedContractId', 'selectedLineItemId']);

  const getContractOptions = async (term) => {
    // Contract-name autocomplete opts into the per-domain flip
    // (apiHostFor('contracts')): the route is ACH-served and 'contracts' is
    // flipped. ACH and Rails both return a BARE {id,label,...} array (no
    // envelope), and this reads only item.label + item.id, so no adaptation.
    const response = await getRequestJSON(
      apiHostFor('contracts'),
      generatePath(API_CONTRACT_SEARCH_URL, {
        '*': `?${queryString.stringify({
          term,
          insertion_order_id: adGroup.insertionOrderId || '',
        })}`,
      })
    );
    return response.map((item) => ({ label: item.label, value: item.id }));
  };

  const getLineItemOptions = async (term) => {
    // The IO-name autocomplete opts into the per-domain flip
    // (apiHostFor('insertionOrders')); the sibling contract search above stays on
    // apiHost — contract search is not part of the ACH-served IO surface. The ACH
    // and Rails responses are both a BARE {id,label,value} array (no envelope), and
    // this reads only item.label + item.id, so no response adaptation is needed.
    const response = await getRequestJSON(
      apiHostFor('insertionOrders'),
      generatePath(API_LINE_ITEM_SEARCH_URL, {
        '*': `?${queryString.stringify({
          term,
          contract_id: selectedContractId || '',
          insertion_order_id: adGroup.insertionOrderId || '',
        })}`,
      })
    );
    return response.map((item) => ({ label: item.label, value: item.id }));
  };

  // Base the warning on the copy response (always carries the full targeting_profile);
  // the `adGroup` prop from the nav can be a lightweight summary without it.
  const cloneWarnings = getAdGroupCloneWarnings(adminCampaign);

  const submitHandler = (formValues) => {
    // copy_time_parting is a form-only deselect toggle and must not be sent to paste.
    const { copy_time_parting: copyTimeParting, ...payload } = formValues;
    if (selectedLineItemId) {
      set(payload, 'admin_campaign.insertion_order_id', selectedLineItemId);
    }
    // ADOPS-2722 — When time parting is deselected, clear the copied hours so the clone
    // starts without time targeting. Handle both snake_case and camelCase profile keys
    // since the copy response shape may vary.
    if (!copyTimeParting) {
      if (get(payload, 'admin_campaign.targeting_profile')) {
        set(payload, 'admin_campaign.targeting_profile.hours', []);
      }
      if (get(payload, 'admin_campaign.targetingProfile')) {
        set(payload, 'admin_campaign.targetingProfile.hours', []);
      }
    }
    pasteMutation.mutate(payload);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Typography variant="h5" mx={2} fontWeight="bold" gutterBottom>
          Duplicate {adGroup.name}
        </Typography>
        <Stack alignItems="flex-start" spacing={2} padding={2}>
          <Paper sx={{ width: '100%', mb: 2 }}>
            <Box p={2}>
              <FormControlLabel
                label="Show Original Ad Group Details"
                control={<Switch onClick={() => setShowDetails(!isShowDetails)} />}
              />
              <Collapse in={isShowDetails} timeout="auto" unmountOnExit>
                <Box sx={{ height: '300px', overflowY: 'scroll' }}>
                  <Stack alignItems="flex-start" spacing={2} py={2}>
                    <TableContainer>
                      <Table>
                        <TableBody>
                          <TableRow key="Name">
                            <TableCell>Name</TableCell>
                            <TableCell>{adGroup.name}</TableCell>
                          </TableRow>
                          <TableRow key="Max Bid">
                            <TableCell>Max Bid</TableCell>
                            <TableCell>{adGroup.maxBid}</TableCell>
                          </TableRow>
                          <TableRow key="State">
                            <TableCell>State</TableCell>
                            <TableCell>{stateString?.state_string}</TableCell>
                          </TableRow>
                          <TableRow key="Many Locations">
                            <TableCell>Many Locations</TableCell>
                            <TableCell>{adGroup.manyLocations.toString()}</TableCell>
                          </TableRow>
                          <TableRow key="Tile Estimate">
                            <TableCell>Tile Estimate</TableCell>
                            <TableCell>{tileEstimate?.tile_estimate}</TableCell>
                          </TableRow>
                          <TableRow key="Conquest">
                            <TableCell>Conquest</TableCell>
                            <TableCell>{adGroup.conquest.toString()}</TableCell>
                          </TableRow>
                          <TableRow key="Campaign URLs">
                            <TableCell>Campaign URLs</TableCell>
                            <TableCell>{JSON.stringify(adGroup.campaignUrls.map((item) => item.value))}</TableCell>
                          </TableRow>
                          <TableRow key="Tracker URLs">
                            <TableCell>Tracker URLs</TableCell>
                            <TableCell>{JSON.stringify(adGroup.trackerUrls)}</TableCell>
                          </TableRow>
                          <TableRow key="Pixel URLs">
                            <TableCell>Pixel URLs</TableCell>
                            <TableCell>{JSON.stringify(adGroup.pixelUrls.map((item) => item.value))}</TableCell>
                          </TableRow>
                          <TableRow key="Tracking Types">
                            <TableCell>Tracking Types</TableCell>
                            <TableCell>{adGroup.trackingTypes}</TableCell>
                          </TableRow>
                          <TableRow key="Targeting">
                            <TableCell sx={{ verticalAlign: 'top' }}>Targeting</TableCell>
                            <TableCell>
                              <TableContainer>
                                <Table>
                                  <TableBody>
                                    {normalizedAdGroupTargeting.map((item) => (
                                      <TableRow key={item.label}>
                                        <TableCell>{item.label}</TableCell>
                                        <TableCell>{item.value}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </Box>
              </Collapse>
            </Box>
          </Paper>
          <CloneFieldWarnings warnings={cloneWarnings} />
          <TextField
            name="admin_campaign.name"
            label="New Ad Group Name"
            sx={{ width: '100%' }}
            rules={{ required: { value: true, message: 'Name is required' } }}
          />
          <Grid container>
            <NumericTextField
              name="number_of_copies"
              label="Copies"
              allowNegative={false}
              decimalScale={0}
              isAllowed={(input) =>
                isBlank(input.floatValue) ||
                (input.floatValue >= defaultValues.number_of_copies && input.floatValue <= 10)
              }
              sx={{ width: '25%', mt: 1 }}
              rules={{ required: { value: true, message: 'Copies is required' } }}
            />
          </Grid>
          <Checkbox name="copy_locations" label="Copy Locations" />
          <Checkbox name="copy_targeted_creatives" label="Copy Targeted Creatives" />
          <Paper sx={{ width: '100%', mb: 2 }}>
            <Box p={2}>
              <FormControlLabel
                label="Duplicate to another Parent Item"
                control={<Switch onClick={() => setCloneDifferentParent(!cloneDifferentParent)} />}
              />
              <Collapse in={cloneDifferentParent} timeout="auto" unmountOnExit>
                <Box sx={{ height: '200px' }}>
                  <Stack alignItems="flex-start" spacing={2} py={2}>
                    <TypeaheadTextField
                      label="Contract"
                      name="selectedContractId"
                      rules={{ required: { value: true, message: 'Contract is required' } }}
                      fullWidth
                      getOptions={getContractOptions}
                    />
                    <TypeaheadTextField
                      label="LineItem"
                      name="selectedLineItemId"
                      rules={{ required: { value: true, message: 'Line Item is required' } }}
                      fullWidth
                      getOptions={getLineItemOptions}
                    />
                  </Stack>
                </Box>
              </Collapse>
            </Box>
          </Paper>
        </Stack>
        <FormEndOptions>
          <Stack direction="row" justifyContent="center" width="100%" spacing={2}>
            <Button variant="outlined" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button type="submit">Duplicate</Button>
          </Stack>
        </FormEndOptions>
      </form>
    </FormProvider>
  );
}

export default CloneAdGroupForm;
